import { resolveScopedFfsArtifactIds } from 'features/libra/stepComponents/ffs/ffsIds'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import {
  DEFAULT_ROLODEX_ENTRY_LIMIT,
  useRolodexEntriesWithSyncedRefreshes,
} from 'features/rolodex'
import { useCallback, useMemo, useState } from 'react'
import { GeckoEntry, RolodexEntryType } from 'types'

import { isBlocking, ScopeRollup } from './ffsResultRollup'
import { ScenarioKey } from './ffsScenarioKey'
import {
  buildUnitRollup,
  ComponentRow,
  rankScenarios,
  ScenarioRank,
} from './ffsUnitAssembly'
import {
  deriveComponents,
  FfsUnitData,
  indexReports,
  matchLtaSets,
  parseLtas,
} from './ffsUnitData'

/**
 * All the data behind the FFS close-out step, for one ticket's whole unit.
 *
 * Libra handles multi-component by REMOUNTING a step per tab, and every existing
 * FFS data hook is strictly per-(component, inspection) — so none of them can be
 * called in a loop here. Close-out sidesteps that entirely: every id it needs is
 * either batch-fetchable or deterministic, so the whole unit resolves in FOUR
 * queries regardless of how many components it has.
 *
 * This hook is deliberately thin: it owns the four queries and the scenario
 * selection, nothing else. Parsing entries into `FfsUnitData` lives in
 * `ffsUnitData`, and ranking/disposition/rollup in `ffsUnitAssembly` — both pure
 * and directly testable.
 */

/**
 * The Rolodex dispenser's own default page size. Queries ask for one MORE than
 * this so a full page proves nothing was cut off; seeing the extra row means we
 * cannot prove completeness and must block rather than roll up a subset.
 * Imported (not copied) so this guard can never silently drift from the default.
 */
const MAX_SUPPORTED = DEFAULT_ROLODEX_ENTRY_LIMIT
const QUERY_LIMIT = MAX_SUPPORTED + 1

export type BlockerKind =
  | 'query-error'
  | 'completeness-unproven'
  | 'unattributable-reports'

export interface CloseOutBlocker {
  kind: BlockerKind
  detail: string
}

export interface UseFfsUnitRollupResult {
  scenarios: ReadonlyArray<ScenarioRank>
  /** The scenario actually in force — the user's pick, else the top-ranked. */
  selectedScenario: ScenarioKey | null
  setSelectedScenario: (key: ScenarioKey) => void
  /** True when the user's pick vanished from the data and the top rank took over. */
  selectionFellBack: boolean
  unitRollup: ScopeRollup
  componentRows: ReadonlyArray<ComponentRow>
  excludedComponents: FfsUnitData['excludedComponents']
  status: 'loading' | 'ready' | 'error' | 'revalidating'
  /**
   * Reasons the step must block that are NOT expressed by `unitRollup` — kept as
   * a list rather than a handful of booleans so the step renders one uniform
   * notice and persistence has a single predicate.
   */
  blockers: ReadonlyArray<CloseOutBlocker>
  /**
   * Trigger-only: resolves once the four refreshes are DISPATCHED, not once the
   * chain re-settles. Q2 can discover a different set id, after which Q3/Q4 are
   * still keyed on the old one until React re-renders — a promise claiming
   * "fresh data is ready" would be lying. Await this, then watch `status`.
   */
  forceRefresh: () => Promise<void>
}

/** Stable primitive key so a fresh array identity does not re-issue a query. */
const idsKey = (ids: ReadonlyArray<string>): string => [...ids].sort().join(',')

/**
 * The dispenser types `entries` more loosely than the `GeckoEntry` shape every
 * parser here needs. Narrowing lives in this one helper so the cast has a single
 * home rather than being repeated at each of the four query call sites.
 */
const asGeckoEntries = (
  entries: ReadonlyArray<unknown>
): ReadonlyArray<GeckoEntry> => entries as ReadonlyArray<GeckoEntry>

export function useFfsUnitRollup(): UseFfsUnitRollupResult {
  const { ticketId, ticketTags } = useTicketAccess()
  const unitId = ticketTags.unit ?? undefined

  // --- Q1: the unit's components and inspections ---------------------------
  // Deliberately NOT `useLibraAssetModelData`: that hook hardcodes its bundle
  // with no `limit` (so it silently inherits the 10k default), adds a
  // units -> sites waterfall, and fetches ASSET_MODEL/SITE/UNIT that close-out
  // never reads. The reusable part is the two pure helpers below, not the fetch.
  const q1 = useRolodexEntriesWithSyncedRefreshes(
    useMemo(
      () =>
        unitId
          ? {
              tags: { unit: unitId },
              type: [
                RolodexEntryType.COMPONENT,
                RolodexEntryType.INSPECTION,
                RolodexEntryType.COMPONENT_DATA_FRAME,
                RolodexEntryType.COMPONENT_DATASET,
              ],
              limit: QUERY_LIMIT,
            }
          : undefined,
      [unitId]
    )
  )

  const { components, excludedComponents } = useMemo(
    () =>
      deriveComponents({
        components: q1.components,
        entries: asGeckoEntries(q1.entries),
        componentDataFrames: q1.componentDataFrames,
        componentDatasets: q1.componentDatasets,
      }),
    [q1.components, q1.entries, q1.componentDataFrames, q1.componentDatasets]
  )

  // --- Q2: which components actually have an LTA set -----------------------
  // `scoped: true` is forced. `resolveFfsArtifactIds` normally derives it from
  // `useComponentScope()`, which is null outside the tabs -- close-out mounts
  // outside them, so the usual call would hand back LEGACY ids and every
  // component would read `not-started`. Close-out targets tabbed tickets only.
  const setIdByComponent = useMemo(() => {
    const map = new Map<string, string>()
    for (const c of components) {
      const { ltaSetId } = resolveScopedFfsArtifactIds({
        ticketId,
        componentId: c.componentId,
      })
      map.set(c.componentId, ltaSetId)
    }
    return map
  }, [components, ticketId])

  const expectedSetIds = useMemo(
    () => [...setIdByComponent.values()],
    [setIdByComponent]
  )
  const expectedSetKey = idsKey(expectedSetIds)

  const q2 = useRolodexEntriesWithSyncedRefreshes(
    useMemo(
      () =>
        expectedSetKey
          ? {
              // No `limit`: an `ids` query defaults to exactly `ids.length`.
              type: RolodexEntryType.INDICATION_SET,
              ids: expectedSetKey.split(','),
            }
          : undefined,
      [expectedSetKey]
    )
  )

  const ltaSetByComponent = useMemo(
    () => matchLtaSets(setIdByComponent, asGeckoEntries(q2.entries)),
    [q2.entries, setIdByComponent]
  )

  // --- Q3: the LTAs in those sets ------------------------------------------
  const foundSetIds = useMemo(
    () => [...ltaSetByComponent.values()].map((s) => s.id),
    [ltaSetByComponent]
  )
  const foundSetKey = idsKey(foundSetIds)

  const q3 = useRolodexEntriesWithSyncedRefreshes(
    useMemo(
      () =>
        foundSetKey
          ? {
              type: RolodexEntryType.INDICATION,
              tags: {
                [RolodexEntryType.INDICATION_SET]: foundSetKey.split(','),
              },
              limit: QUERY_LIMIT,
            }
          : undefined,
      [foundSetKey]
    )
  )

  const { ltasByComponent, ltaIds } = useMemo(
    () => parseLtas(asGeckoEntries(q3.entries), ltaSetByComponent),
    [q3.entries, ltaSetByComponent]
  )

  const ltaKey = idsKey(ltaIds)

  // --- Q4: every report tagged to those LTAs -------------------------------
  const q4 = useRolodexEntriesWithSyncedRefreshes(
    useMemo(
      () =>
        ltaKey
          ? {
              type: RolodexEntryType.FFS_LTA_REPORT,
              tags: { [RolodexEntryType.INDICATION]: ltaKey.split(',') },
              limit: QUERY_LIMIT,
            }
          : undefined,
      [ltaKey]
    )
  )

  const reportIndex = useMemo(
    () => indexReports(asGeckoEntries(q4.entries)),
    [q4.entries]
  )

  // --- Assembly (pure) -----------------------------------------------------
  const data = useMemo<FfsUnitData>(
    () => ({
      components,
      excludedComponents,
      ltaSetByComponent,
      ltasByComponent,
      ...reportIndex,
    }),
    [
      components,
      excludedComponents,
      ltaSetByComponent,
      ltasByComponent,
      reportIndex,
    ]
  )

  const scenarios = useMemo(() => rankScenarios(data), [data])

  // State holds only an EXPLICIT user pick. The scenario in force is derived, so
  // a pick that vanishes from the data falls back to the top rank without an
  // effect, a reset, or a render where the selection points at nothing.
  const [picked, setPicked] = useState<ScenarioKey | null>(null)
  const pickIsValid = picked !== null && scenarios.some((s) => s.key === picked)
  const selectedScenario = pickIsValid ? picked : (scenarios[0]?.key ?? null)

  const { unitRollup, componentRows } = useMemo(
    () => buildUnitRollup(data, selectedScenario),
    [data, selectedScenario]
  )

  // --- Status and blockers -------------------------------------------------
  // No intermediate `queries` array: the query results are fresh objects each
  // render, so collecting them into one would make every downstream memo churn.
  // Depending on the specific fields read keeps the dependencies honest.
  const firstError = q1.error ?? q2.error ?? q3.error ?? q4.error
  const errorMessage = firstError?.message

  const status: UseFfsUnitRollupResult['status'] = firstError
    ? 'error'
    : q1.isLoading || q2.isLoading || q3.isLoading || q4.isLoading
      ? 'loading'
      : q1.isValidating || q2.isValidating || q3.isValidating || q4.isValidating
        ? 'revalidating'
        : 'ready'

  // A full page means the server may have had more to give. We cannot prove
  // completeness, so we say exactly that rather than asserting truncation.
  const completenessUnproven =
    q1.entries.length > MAX_SUPPORTED ||
    q3.entries.length > MAX_SUPPORTED ||
    q4.entries.length > MAX_SUPPORTED

  const { unattributableReportFailures } = reportIndex

  const blockers = useMemo((): ReadonlyArray<CloseOutBlocker> => {
    const found: CloseOutBlocker[] = []
    if (errorMessage) {
      found.push({
        kind: 'query-error',
        detail: `Could not load close-out data from Rolodex: ${errorMessage}`,
      })
    }
    if (completenessUnproven) {
      found.push({
        kind: 'completeness-unproven',
        detail: `This unit returned more than ${MAX_SUPPORTED.toLocaleString()} records, so close-out cannot prove it read all of them.`,
      })
    }
    if (unattributableReportFailures > 0) {
      found.push({
        kind: 'unattributable-reports',
        detail: `${unattributableReportFailures} saved FFS report(s) could not be read or linked to an LTA.`,
      })
    }
    return found
  }, [errorMessage, completenessUnproven, unattributableReportFailures])

  // Each `forceRefresh` is a stable identity (the synced-refresh hook wraps it
  // in its own `useCallback`), so binding the four to locals lets this callback
  // depend on those stable functions directly — keeping it (and the return memo
  // below) stable across renders, unlike the previous fresh-array-per-render.
  const refresh1 = q1.forceRefresh
  const refresh2 = q2.forceRefresh
  const refresh3 = q3.forceRefresh
  const refresh4 = q4.forceRefresh
  const forceRefresh = useCallback(async () => {
    await Promise.all([refresh1(), refresh2(), refresh3(), refresh4()])
  }, [refresh1, refresh2, refresh3, refresh4])

  const setSelectedScenario = useCallback(
    (key: ScenarioKey) => setPicked(key),
    []
  )

  return useMemo(
    () => ({
      scenarios,
      selectedScenario,
      setSelectedScenario,
      selectionFellBack: picked !== null && !pickIsValid,
      unitRollup,
      componentRows,
      excludedComponents,
      status,
      blockers,
      forceRefresh,
    }),
    [
      scenarios,
      selectedScenario,
      setSelectedScenario,
      picked,
      pickIsValid,
      unitRollup,
      componentRows,
      excludedComponents,
      status,
      blockers,
      forceRefresh,
    ]
  )
}

/**
 * The single "may this be recorded?" predicate for the step and persistence.
 *
 * Takes only the three fields it reads, so it can be called with the whole hook
 * result without pretending to depend on the rest of it.
 */
export const canRecordResult = ({
  status,
  blockers,
  unitRollup,
}: Pick<
  UseFfsUnitRollupResult,
  'status' | 'blockers' | 'unitRollup'
>): boolean =>
  status === 'ready' && blockers.length === 0 && !isBlocking(unitRollup)
